import { type dateRangeDto, LaneConfig, type StoreControl, ZoomSliderMode } from 'commons';
import { sub } from 'date-fns';
import type { EChartsOption } from 'echarts';
import { immerable } from 'immer';
import { v4 } from 'uuid';
import { multilane_config } from '../../../components/dtis-multilane-chart/multi-lane.config';
import type { DtisZoomEvent } from '../../../components/dtis-multilane-chart/zoomEvent';
import type { KPI_Table } from '../../../components/dtis-multilane-viewer/KPI_Table';
import type { CommentModel } from '../../../services/api/models/CommentModel';
import type { pageType } from './pageType';

export interface ViewHistoryMeta {
  actionType: string;
  timestamp: number;
}

/**
 * A snapshot of a SingleView without the per-view undo/redo stacks.
 * Used as entries in the history and future arrays to avoid circular nesting.
 */
export type SingleViewSnapshot = Omit<ISingleView, 'history' | 'historyMeta' | 'future' | 'futureMeta'>;

export type styleTargets = 'sensortable' | 'datatree';
export type cssProps = 'height' | 'width';

export type cssPropValue = {
  [S in cssProps]?: string;
};

export type viewStyle = {
  [K in styleTargets]?: cssPropValue;
};

export interface ISingleView {
  /** Unique identifier for the view. */
  id: string | undefined;

  /** The owner of the view. */
  owner: string;

  /** List of users the view is shared with. */
  sharedWith: string[];

  /** Array of loading states. */
  isLoading: string[];

  /** Label or title of the view. */
  label: string;

  /** Date range associated with the view. */
  dateRange: dateRangeDto;

  /** Mode of the zoom slider. */
  zoomSliderMode: ZoomSliderMode;

  /** Configuration for ECharts visualization. */
  echartsConfig: EChartsOption;

  /** Indicates if comment mode is enabled. */
  commentModeEnabled: boolean;

  /** Indicates if comments are currently being loaded from the API. */
  isLoadingComments: boolean;

  /** Indicates if test phases are visible. */
  testPhasesVisible: boolean;

  /** Configuration for lanes in the view. */
  lanes: LaneConfig[];

  /** Data for the sensor table. */
  sensorTable: StoreControl[];

  /** List of comments associated with the view. */
  comments: CommentModel[];

  /** Timestamp of the last visit to the view. */
  lastVisted: Date;

  /** Timestamp of the last successful telemetry fetch that was rendered in the chart. */
  lastTelemetryFetchedAt?: Date;

  /** Timestamp of the newest telemetry data available on the backend, set once a background check finds newer data than `lastTelemetryFetchedAt`. Undefined while no newer data is known. */
  newDataAvailableSince?: Date;

  /** State of the zoom functionality. */
  zoomState: DtisZoomEvent[];

  /** Custom styles for the view. */
  styles: viewStyle;

  /** Type of the page associated with the view. */
  pageType: pageType;

  /** Selected lane filter for table view - 'all' means show all lanes, otherwise specific lane ID */
  tableViewLaneFilter: string;

  /** Undo history for this view. Not persisted. Most recent snapshot at end. Max 20 entries. */
  history: SingleViewSnapshot[];
  /** Metadata parallel to history (action name + timestamp). Not persisted. */
  historyMeta: ViewHistoryMeta[];
  /** Redo stack for this view. Not persisted. Most recent undone at start. */
  future: SingleViewSnapshot[];
  /** Metadata parallel to future. Not persisted. */
  futureMeta: ViewHistoryMeta[];
}

export class SingleView implements ISingleView {
  [immerable] = true;

  /** Unique identifier for the view. */
  id = '';

  /** Timestamp of the last visit to the view. */
  lastVisted = new Date();

  /** Array of loading states. */
  isLoading: string[] = [];

  /** Label or title of the view. */
  label = 'Title';

  /** State of the zoom functionality. */
  zoomState = [
    {
      start: 0,
      end: 100,
      startValue: undefined,
      endValue: undefined,
      axis: ['0'],
      dataZoomId: 'zoom_0_0',
    },
  ];

  /** Identifier for the last zoomed element. */
  lastZoomedId = '';

  /** Timestamp of the last successful telemetry fetch that was rendered in the chart. */
  lastTelemetryFetchedAt?: Date;

  /** Timestamp of the newest telemetry data available on the backend, undefined while no newer data is known. */
  newDataAvailableSince?: Date;

  /** Custom styles for the view. */
  styles = {
    sensortable: {
      height: 'fit-content',
    },
    datatree: {
      width: '15%',
    },
  };

  /** Indicates if comment mode is enabled. */
  commentModeEnabled = false;

  /** Indicates if comments are currently being loaded from the API. */
  isLoadingComments = false;

  /** Indicates if test phases are visible. */
  testPhasesVisible = false;

  /** Mode of the zoom slider. */
  zoomSliderMode = ZoomSliderMode.GLOBAL;

  /** Configuration for ECharts visualization. */
  echartsConfig: EChartsOption = { ...multilane_config };

  /** Configuration for lanes in the view. */
  lanes = [new LaneConfig({ isActive: true })];

  /** Data for the KPI table. */
  kpiTable: KPI_Table = {};

  /** List of comments associated with the view. */
  comments: CommentModel[] = [];

  /** Data for the sensor table. */
  sensorTable: StoreControl[] = [];

  /** The owner of the view. */
  owner: string = '';

  /** List of users the view is shared with. */
  sharedWith: string[] = [];

  /** Date range associated with the view. */
  dateRange: dateRangeDto = {
    start_date: sub(new Date(), { months: 1 }),
    end_date: new Date(),
  };

  /** Type of the page associated with the view. */
  pageType: pageType = 'linechart';

  /** Selected lane filter for table view - 'all' means show all lanes, otherwise specific lane ID */
  tableViewLaneFilter: string = 'all';

  /** Undo history for this view. Not persisted. */
  history: SingleViewSnapshot[] = [];
  /** Metadata parallel to history. Not persisted. */
  historyMeta: ViewHistoryMeta[] = [];
  /** Redo stack for this view. Not persisted. */
  future: SingleViewSnapshot[] = [];
  /** Metadata parallel to future. Not persisted. */
  futureMeta: ViewHistoryMeta[] = [];

  /**
   * Constructor to initialize a SingleView instance.
   * @param args Partial properties to initialize the view.
   */
  constructor(args: Partial<SingleView> = {}) {
    Object.assign(this, args);
    if (!this.id) {
      this.id = v4();
    }
    if (!this.sensorTable) {
      this.sensorTable = [];
    }
    if (!this.zoomState) {
      this.zoomState = [
        {
          start: 0,
          end: 100,
          startValue: undefined,
          endValue: undefined,
          axis: ['0'],
          dataZoomId: 'zoom_0_0',
        },
      ];
    }
  }
}
